/**
 * Normalize tariff payloads between API shape and editor-friendly structures.
 */
import type {
  BuyPricesMap,
  BuyTemplatePrices,
  CallPrices,
  PackagePrice,
  ScoringPrices,
  SmsPriceStep,
  SmsPrices,
  SmsPriceEntry,
  AggregatorSmsTypeMap,
  SmsTypeBillingBlock,
  SmsTypeBillingMap,
} from './types';
import { isSmsType, parseSmsTypeWireKey, smsTypeWireKey } from '../constants/sms-types';

export interface BuyPriceEntry<TPrices> {
  /** Aggregator id (numeric, comes from SMS API `/aggregators/`). */
  aggregator_id: number;
  payload: TPrices;
}

/** API map → editor array (sorted by aggregator_id for stable ordering). */
export function buyPricesToArray<TPrices>(
  map: BuyPricesMap<TPrices> | undefined | null,
): BuyPriceEntry<TPrices>[] {
  if (!map) return [];
  return Object.entries(map)
    .map(([k, v]) => ({ aggregator_id: Number(k), payload: v }))
    .filter((e) => Number.isFinite(e.aggregator_id))
    .sort((a, b) => a.aggregator_id - b.aggregator_id);
}

/**
 * Editor array → API map.
 * Drops entries that are empty. Stringifies aggregator_id keys (the wire format).
 */
export function arrayToBuyPrices<TPrices>(
  list: BuyPriceEntry<TPrices>[] | undefined | null,
  isEmpty: (payload: TPrices) => boolean,
): BuyPricesMap<TPrices> {
  const out: BuyPricesMap<TPrices> = {};
  if (!list) return out;
  for (const e of list) {
    if (!Number.isFinite(e.aggregator_id) || e.aggregator_id <= 0) continue;
    if (isEmpty(e.payload)) continue;
    out[String(e.aggregator_id)] = e.payload;
  }
  return out;
}

// -- Emptiness predicates per category --------------------------------------

export function isAggregatorSmsTypeMapEmpty(
  map: AggregatorSmsTypeMap | undefined | null,
): boolean {
  if (!map) return true;
  for (const typeMap of Object.values(map)) {
    for (const billing of Object.values(typeMap ?? {})) {
      if (billing?.steps?.length || billing?.packages?.length) return false;
    }
  }
  return true;
}

export function isSmsTypeBillingMapEmpty(map: SmsTypeBillingMap | undefined | null): boolean {
  if (!map) return true;
  for (const billing of Object.values(map)) {
    if (billing?.steps?.length || billing?.packages?.length) return false;
  }
  return true;
}

/** @deprecated Use isAggregatorSmsTypeMapEmpty */
export const isSmsTypeAggregatorMapEmpty = isAggregatorSmsTypeMapEmpty;

/** @deprecated Legacy per-aggregator steps shape. */
export function isSmsBuyPricesEmpty(p: SmsPrices): boolean {
  return !p?.steps || p.steps.length === 0;
}

export function isCallBuyPricesEmpty(p: CallPrices): boolean {
  if (!p) return true;
  return (
    (!p.phone_rent || p.phone_rent === 0) &&
    (!p.income_calls || p.income_calls === 0) &&
    (!p.outcome_calls || p.outcome_calls === 0)
  );
}

export function isScoringBuyPricesEmpty(p: ScoringPrices): boolean {
  if (!p) return true;
  return (
    (!p.inner_score || p.inner_score === 0) &&
    (!p.operator_score || p.operator_score === 0)
  );
}

// -- SMS type × aggregator billing (per-type steps + packages) ----------------

export interface SmsTypePricesUi {
  steps: SmsPriceStep[];
  packages: PackagePrice[];
}

export interface AggregatorSmsBillingUi {
  aggregator_id: number;
  selectedTypes: number[];
  pricesByType: Record<number, SmsTypePricesUi>;
}

export interface SmsTypeBillingUiState {
  blocks: AggregatorSmsBillingUi[];
}

export function emptySmsTypeBillingUi(): SmsTypeBillingUiState {
  return { blocks: [] };
}

export function emptySmsTypePrices(): SmsTypePricesUi {
  return { steps: [{ from: 0, to: null, price: 0 }], packages: [] };
}

export interface SmsTypeOnlyBillingUi {
  selectedTypes: number[];
  pricesByType: Record<number, SmsTypePricesUi>;
}

export function emptySmsTypeOnlyBillingUi(): SmsTypeOnlyBillingUi {
  return { selectedTypes: [], pricesByType: {} };
}

export function deepCloneAggregatorSmsTypeMap(
  map: AggregatorSmsTypeMap | undefined | null,
): AggregatorSmsTypeMap {
  if (!map) return {};
  return structuredClone(map);
}

export function deepCloneSmsTypeBillingMap(
  map: SmsTypeBillingMap | undefined | null,
): SmsTypeBillingMap {
  if (!map) return {};
  return structuredClone(map);
}

/** @deprecated Use deepCloneAggregatorSmsTypeMap */
export const deepCloneSmsTypeAggregatorMap = deepCloneAggregatorSmsTypeMap;

function wireStep(s: SmsPriceStep): SmsPriceStep {
  return { from: s.from, to: s.to ?? null, price: s.price };
}

function wirePackage(p: PackagePrice): PackagePrice {
  return { from: p.from, to: p.to, price: p.price };
}

function sanitizeBillingBlock(
  billing: SmsTypeBillingBlock | undefined | null,
): SmsTypeBillingBlock {
  const out: SmsTypeBillingBlock = {};
  if (billing?.steps?.length) {
    out.steps = billing.steps.map(wireStep);
  }
  if (billing?.packages?.length) {
    out.packages = billing.packages.map(wirePackage);
  }
  return out;
}

function buildWireBillingBlock(entry: SmsTypePricesUi): SmsTypeBillingBlock | null {
  const billing: SmsTypeBillingBlock = {};

  const steps = (entry.steps ?? [])
    .filter((s) => s.from != null && Number.isFinite(s.price))
    .map(wireStep);

  if (steps.length) {
    billing.steps = normalizeSmsSteps(steps);
  } else {
    billing.steps = [{ from: 0, to: null, price: 0 }];
  }

  const packages = (entry.packages ?? [])
    .filter((p) => p.from != null && p.to != null && Number.isFinite(p.price))
    .map(wirePackage);
  if (packages.length) billing.packages = packages;

  if (!billing.steps?.length && !billing.packages?.length) return null;
  return billing;
}

/** Detect legacy layouts and normalize sms_type keys to numeric string ids. */
export function toAggregatorSmsTypeMap(
  map: AggregatorSmsTypeMap | undefined | null,
): AggregatorSmsTypeMap {
  if (!map) return {};
  const topKeys = Object.keys(map);
  if (topKeys.length === 0) return {};

  let aggFirst = map;
  if (topKeys.some(isSmsType)) {
    const flipped: AggregatorSmsTypeMap = {};
    for (const [smsKey, aggMap] of Object.entries(map)) {
      const typeId = parseSmsTypeWireKey(smsKey);
      if (typeId == null) continue;
      const typeKey = smsTypeWireKey(typeId);
      for (const [aggKey, billing] of Object.entries(aggMap ?? {})) {
        if (!flipped[aggKey]) flipped[aggKey] = {};
        flipped[aggKey][typeKey] = sanitizeBillingBlock(billing);
      }
    }
    aggFirst = flipped;
  }

  const out: AggregatorSmsTypeMap = {};
  for (const [aggKey, typeMap] of Object.entries(aggFirst)) {
    const normalized: Record<string, SmsTypeBillingBlock> = {};
    for (const [typeKey, billing] of Object.entries(typeMap ?? {})) {
      const typeId = parseSmsTypeWireKey(typeKey);
      if (typeId == null) continue;
      normalized[smsTypeWireKey(typeId)] = sanitizeBillingBlock(billing);
    }
    if (Object.keys(normalized).length) out[aggKey] = normalized;
  }
  return out;
}

function looksLikeBillingBlock(value: unknown): value is SmsTypeBillingBlock {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const obj = value as Record<string, unknown>;
  return Array.isArray(obj.steps) || Array.isArray(obj.packages) || 'steps' in obj || 'packages' in obj;
}

/** Normalize sell map: sms_type_id → billing. Flattens legacy aggregator→type if needed. */
export function toSmsTypeBillingMap(
  map: SmsTypeBillingMap | AggregatorSmsTypeMap | undefined | null,
): SmsTypeBillingMap {
  if (!map || typeof map !== 'object') return {};
  const out: SmsTypeBillingMap = {};

  for (const [key, value] of Object.entries(map)) {
    if (looksLikeBillingBlock(value)) {
      const typeId = parseSmsTypeWireKey(key);
      if (typeId == null) continue;
      const billing = sanitizeBillingBlock(value);
      if (billing.steps?.length || billing.packages?.length) {
        out[smsTypeWireKey(typeId)] = billing;
      }
      continue;
    }
    // Legacy nested aggregator → type map: merge types.
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      for (const [typeKey, billing] of Object.entries(value as Record<string, SmsTypeBillingBlock>)) {
        const typeId = parseSmsTypeWireKey(typeKey);
        if (typeId == null || !looksLikeBillingBlock(billing)) continue;
        const sanitized = sanitizeBillingBlock(billing);
        if (sanitized.steps?.length || sanitized.packages?.length) {
          out[smsTypeWireKey(typeId)] = sanitized;
        }
      }
    }
  }
  return out;
}

export function smsTypeBillingMapToUi(
  map: SmsTypeBillingMap | AggregatorSmsTypeMap | undefined | null,
): SmsTypeOnlyBillingUi {
  const normalized = toSmsTypeBillingMap(map);
  const selectedTypes: number[] = [];
  const pricesByType: Record<number, SmsTypePricesUi> = {};

  for (const typeKey of Object.keys(normalized)) {
    const typeId = parseSmsTypeWireKey(typeKey);
    if (typeId == null) continue;
    const billing = normalized[typeKey];
    if (!billing?.steps?.length && !billing?.packages?.length) continue;
    selectedTypes.push(typeId);
    pricesByType[typeId] = {
      steps: (billing.steps ?? []).map(wireStep),
      packages: (billing.packages ?? []).map(wirePackage),
    };
  }

  return {
    selectedTypes: selectedTypes.sort((a, b) => a - b),
    pricesByType,
  };
}

export function uiToSmsTypeBillingMap(state: SmsTypeOnlyBillingUi): SmsTypeBillingMap {
  const out: SmsTypeBillingMap = {};
  for (const smsTypeId of state.selectedTypes) {
    const entry = state.pricesByType[smsTypeId];
    if (!entry) continue;
    const billing = buildWireBillingBlock(entry);
    if (!billing) continue;
    out[smsTypeWireKey(smsTypeId)] = billing;
  }
  return out;
}

export function aggregatorSmsTypeMapToUi(
  map: AggregatorSmsTypeMap | undefined | null,
): SmsTypeBillingUiState {
  const normalized = toAggregatorSmsTypeMap(map);
  if (Object.keys(normalized).length === 0) return emptySmsTypeBillingUi();

  const aggregatorIds = Object.keys(normalized)
    .map(Number)
    .filter((id) => Number.isFinite(id) && id > 0)
    .sort((a, b) => a - b);

  const blocks: AggregatorSmsBillingUi[] = aggregatorIds.map((aggregator_id) => {
    const typeMap = normalized[String(aggregator_id)] ?? {};
    const selectedTypes: number[] = [];
    const pricesByType: Record<number, SmsTypePricesUi> = {};

    for (const typeKey of Object.keys(typeMap)) {
      const typeId = parseSmsTypeWireKey(typeKey);
      if (typeId == null) continue;
      const billing = typeMap[typeKey];
      if (!billing?.steps?.length && !billing?.packages?.length) continue;
      selectedTypes.push(typeId);
      pricesByType[typeId] = {
        steps: (billing.steps ?? []).map(wireStep),
        packages: (billing.packages ?? []).map(wirePackage),
      };
    }

    return {
      aggregator_id,
      selectedTypes,
      pricesByType,
    };
  });

  return { blocks };
}

/** @deprecated Use aggregatorSmsTypeMapToUi */
export const smsTypeAggregatorMapToUi = aggregatorSmsTypeMapToUi;

export function uiToAggregatorSmsTypeMap(state: SmsTypeBillingUiState): AggregatorSmsTypeMap {
  const out: AggregatorSmsTypeMap = {};

  for (const block of state.blocks) {
    if (!block.aggregator_id) continue;
    const aggKey = String(block.aggregator_id);
    const typeMap: Record<string, SmsTypeBillingBlock> = {};

    for (const smsTypeId of block.selectedTypes) {
      const entry = block.pricesByType[smsTypeId];
      if (!entry) continue;
      const billing = buildWireBillingBlock(entry);
      if (!billing) continue;
      typeMap[smsTypeWireKey(smsTypeId)] = billing;
    }

    if (Object.keys(typeMap).length) out[aggKey] = typeMap;
  }

  return out;
}

/** @deprecated Use uiToAggregatorSmsTypeMap */
export const uiToSmsTypeAggregatorMap = uiToAggregatorSmsTypeMap;

export function collectSmsTypesFromMap(map: AggregatorSmsTypeMap | undefined | null): number[] {
  const types = new Set<number>();
  const normalized = toAggregatorSmsTypeMap(map);
  for (const typeMap of Object.values(normalized)) {
    for (const typeKey of Object.keys(typeMap ?? {})) {
      const id = parseSmsTypeWireKey(typeKey);
      if (id != null) types.add(id);
    }
  }
  return [...types].sort((a, b) => a - b);
}

// -- Buy-template calls (country_iso → operator_id → price) -------------------

export interface BuyTemplateCallEntry {
  country_iso: string;
  operator_id: number;
  price: number;
}

export function buyTemplateCallsToArray(
  map: BuyTemplatePrices['calls'] | undefined | null,
): BuyTemplateCallEntry[] {
  if (!map) return [];
  const result: BuyTemplateCallEntry[] = [];
  for (const [country_iso, operators] of Object.entries(map)) {
    for (const [opId, price] of Object.entries(operators ?? {})) {
      const operator_id = Number(opId);
      if (!Number.isFinite(operator_id) || operator_id <= 0) continue;
      result.push({ country_iso, operator_id, price: Number(price) || 0 });
    }
  }
  return result.sort(
    (a, b) => a.country_iso.localeCompare(b.country_iso) || a.operator_id - b.operator_id,
  );
}

export function buyTemplateCallsToMap(
  list: BuyTemplateCallEntry[] | undefined | null,
): NonNullable<BuyTemplatePrices['calls']> {
  const out: NonNullable<BuyTemplatePrices['calls']> = {};
  if (!list) return out;
  for (const e of list) {
    if (!e.country_iso || !e.operator_id) continue;
    if (!e.price) continue;
    if (!out[e.country_iso]) out[e.country_iso] = {};
    out[e.country_iso][String(e.operator_id)] = e.price;
  }
  return out;
}

/** Buy-template `prices.sms`: aggregator_id → sms_type → billing. */
export function buyTemplateSmsFromPrices(
  prices: BuyTemplatePrices | undefined | null,
): AggregatorSmsTypeMap {
  return toAggregatorSmsTypeMap((prices?.sms ?? {}) as AggregatorSmsTypeMap);
}

export function buyTemplateSmsToPrices(
  sms: AggregatorSmsTypeMap,
): NonNullable<BuyTemplatePrices['sms']> {
  return toAggregatorSmsTypeMap(sms) as NonNullable<BuyTemplatePrices['sms']>;
}

// -- Step utilities -----------------------------------------------------------

export function normalizeSmsSteps(steps: SmsPriceStep[]): SmsPriceStep[] {
  if (!Array.isArray(steps) || steps.length === 0) return [];
  const sorted = [...steps].sort((a, b) => (a.from ?? 0) - (b.from ?? 0));
  for (let i = 0; i < sorted.length - 1; i++) {
    if (sorted[i].to == null) sorted[i] = { ...sorted[i], to: sorted[i + 1].from };
  }
  sorted[sorted.length - 1] = { ...sorted[sorted.length - 1], to: null };
  return sorted;
}

/** True when `prices` is the legacy `{ steps: [...] }` sell shape. */
export function isLegacySmsPrices(prices: SmsPriceEntry['prices'] | undefined): prices is SmsPrices {
  return Boolean(prices && 'steps' in prices && Array.isArray((prices as SmsPrices).steps));
}
